import { useMemo, useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { Map as MapIcon, List, Loader2, MapPin } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { MappingProgressBar } from "@/components/mapping/MappingProgressBar";
import { MapperMap } from "@/components/mapping/MapperMap";
import { LocationListItem } from "@/components/mapping/LocationListItem";
import { DirectionsLink } from "@/components/mapping/DirectionsLink";
import { HelpFAB } from "@/components/mapping/HelpFAB";
import { Button } from "@/components/ui/button";
import { haversineDistance } from "@/lib/geo";
import { useUserPosition } from "@/hooks/useUserPosition";
import { useForceLightTheme } from "@/hooks/useForceLightTheme";
import { cn } from "@/lib/utils";

type View = "map" | "list";

export function MappingHomePage() {
  useForceLightTheme();
  const params = useParams({ strict: false });
  const token = (params as { token?: string }).token as string;
  const navigate = useNavigate();

  const [view, setView] = useState<View>("map");
  const userPos = useUserPosition();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: collection, isLoading: colLoading, error: colError } =
    trpc.share.getCollection.useQuery({ token }, { enabled: !!token });

  const { data: mappingData, isLoading: locsLoading } =
    trpc.share.listMappingLocations.useQuery({ token }, { enabled: !!token });
  const locations = mappingData?.locations;

  const enriched = useMemo(() => {
    const list = (locations ?? []) as Array<{
      id: string;
      name: string;
      address: string | null;
      lat: number;
      lng: number;
      mapping_status: string;
      pil_total: number;
      pil_done: number;
    }>;
    return list.map((loc) => ({
      ...loc,
      pil_count: loc.pil_total,
      distanceKm: userPos
        ? haversineDistance(userPos.lat, userPos.lng, loc.lat, loc.lng) / 1000
        : null,
    }));
  }, [locations, userPos]);

  const sorted = useMemo(() => {
    const copy = [...enriched];
    if (userPos) {
      copy.sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
    } else {
      copy.sort((a, b) => a.name.localeCompare(b.name));
    }
    return copy;
  }, [enriched, userPos]);

  const stats = useMemo(() => {
    const total = enriched.length;
    // From the mapper's POV, both "in_progress" and "done" count as done —
    // in_progress means they've submitted at least one spot; the admin does
    // the final review later.
    const done = enriched.filter(
      (l) => l.mapping_status === "done" || l.mapping_status === "in_progress",
    ).length;
    return { total, done };
  }, [enriched]);

  const selectedLoc = useMemo(
    () => enriched.find((l) => l.id === selectedId) ?? null,
    [enriched, selectedId],
  );

  const mapCenter = useMemo(() => {
    if (userPos) return userPos;
    if (enriched.length > 0) {
      return {
        lat: enriched.reduce((s, l) => s + l.lat, 0) / enriched.length,
        lng: enriched.reduce((s, l) => s + l.lng, 0) / enriched.length,
      };
    }
    return undefined;
  }, [userPos, enriched]);

  if (colLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (colError || !collection) {
    return <UnavailableScreen title="Link invalid" message="This mapping link is invalid or has expired." />;
  }

  const mappingEnabled = (collection as any).mapping_share_enabled !== false;
  if (!mappingEnabled) {
    return <UnavailableScreen />;
  }

  return (
    <div className="flex h-[100dvh] flex-col bg-background">
      <header className="flex shrink-0 flex-col gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span
            className="h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: collection.color }}
          />
          <h1 className="text-base font-semibold truncate flex-1">{collection.name}</h1>
          <MappingProgressBar done={stats.done} total={stats.total} />
        </div>
        <div className="flex gap-1 rounded-md bg-muted p-1">
          <button
            type="button"
            onClick={() => setView("map")}
            className={cn(
              "flex-1 inline-flex items-center justify-center gap-2 min-h-11 rounded text-sm font-medium transition-colors",
              view === "map" ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            <MapIcon className="h-4 w-4" />
            Map
          </button>
          <button
            type="button"
            onClick={() => setView("list")}
            className={cn(
              "flex-1 inline-flex items-center justify-center gap-2 min-h-11 rounded text-sm font-medium transition-colors",
              view === "list" ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            <List className="h-4 w-4" />
            List
          </button>
        </div>
      </header>

      <main className="relative flex-1 overflow-hidden">
        {locsLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : enriched.length === 0 ? (
          <EmptyState />
        ) : view === "map" ? (
          <>
            <MapperMap
              className="h-full w-full"
              defaultCenter={mapCenter}
              defaultZoom={userPos ? 14 : 12}
              locations={enriched.map((l) => ({
                id: l.id,
                lat: l.lat,
                lng: l.lng,
                status: l.mapping_status,
              }))}
              userPos={userPos}
              showRecenterButton
              onLocationClick={(id) => setSelectedId(id)}
            />
            {selectedLoc && (
              <div className="absolute inset-x-2 bottom-2 rounded-xl border bg-background p-3 shadow-lg">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <h2 className="font-semibold text-sm truncate">{selectedLoc.name}</h2>
                    </div>
                    {selectedLoc.address && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {selectedLoc.address}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedId(null)}
                    className="text-xs text-muted-foreground min-h-11 px-2"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="mt-3 flex gap-2">
                  <DirectionsLink
                    lat={selectedLoc.lat}
                    lng={selectedLoc.lng}
                    name={selectedLoc.name}
                    className="flex-1"
                  />
                  <Button
                    className="flex-1 min-h-11"
                    onClick={() =>
                      navigate({
                        to: "/mapping/$token/location/$locationId",
                        params: { token, locationId: selectedLoc.id },
                      } as any)
                    }
                  >
                    Start mapping
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="h-full overflow-auto">
            {sorted.map((loc) => (
              <LocationListItem
                key={loc.id}
                name={loc.name}
                address={loc.address}
                lat={loc.lat}
                lng={loc.lng}
                status={loc.mapping_status}
                pilCount={loc.pil_count}
                distanceKm={loc.distanceKm}
                onClick={() =>
                  navigate({
                    to: "/mapping/$token/location/$locationId",
                    params: { token, locationId: loc.id },
                  } as any)
                }
              />
            ))}
          </div>
        )}
      </main>
      <HelpFAB token={token} />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <MapPin className="h-10 w-10 text-muted-foreground" />
      <p className="text-sm font-medium">No locations yet</p>
      <p className="text-xs text-muted-foreground">
        Your admin hasn&rsquo;t added any locations to this mapping session yet.
      </p>
    </div>
  );
}

function UnavailableScreen({
  title = "This mapping session has ended",
  message = "Please contact your admin.",
}: {
  title?: string;
  message?: string;
}) {
  return (
    <div className="flex h-[100dvh] flex-col items-center justify-center gap-3 px-6 text-center bg-background">
      <MapIcon className="h-12 w-12 text-muted-foreground" />
      <h1 className="text-lg font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
